import type { ReactElement, ReactNode } from 'react';
import clsx from 'clsx';
import { ResponsiveContainer } from 'recharts';
import { Card, CardHeader } from '../Card';
import { Spinner } from '../Spinner';
import { CHART_HEIGHT } from './palette';

interface ChartCardProps {
  title: string;
  description?: string;
  /** Rendered top-right of the header — a range selector, a legend toggle. */
  action?: ReactNode;
  /** Chart height in px. Prefer a CHART_HEIGHT value so panels align across dashboards. */
  height?: number;
  isLoading?: boolean;
  /** When true, the empty label replaces the chart. Cheaper than each caller branching. */
  isEmpty?: boolean;
  emptyLabel?: string;
  className?: string;
  /** Content rendered between the header and the chart, e.g. a headline stat row. */
  children?: ReactNode;
  /** A single recharts chart element. Omit it to render a card with no chart. */
  chart?: ReactElement;
  /** Content rendered below the chart, e.g. a caption or a legend. */
  footer?: ReactNode;
}

/**
 * A titled card wrapping one chart at a shared height, with loading and empty
 * states handled once. Replaces the `<Card> + inline <ResponsiveContainer>`
 * pattern that was repeated across every dashboard at five different heights.
 */
export function ChartCard({
  title,
  description,
  action,
  height = CHART_HEIGHT.default,
  isLoading = false,
  isEmpty = false,
  emptyLabel = 'No data for this period.',
  className,
  children,
  chart,
  footer,
}: ChartCardProps) {
  return (
    <Card className={className}>
      <CardHeader title={title} description={description} action={action} />
      {children}
      {isLoading ? (
        <div className="flex items-center justify-center" style={{ height }}>
          <Spinner />
        </div>
      ) : isEmpty || !chart ? (
        <ChartEmpty label={emptyLabel} height={height} />
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {chart}
        </ResponsiveContainer>
      )}
      {footer}
    </Card>
  );
}

/** The shared empty state for any chart slot, inside or outside a ChartCard. */
export function ChartEmpty({ label, height }: { label: string; height?: number }) {
  return (
    <div
      className={clsx('flex items-center justify-center text-body-sm text-text-tertiary', !height && 'py-8')}
      style={height ? { height } : undefined}
    >
      {label}
    </div>
  );
}

/** A small uppercase label for a chart nested inside a larger card. */
export function ChartSubtitle({ children }: { children: ReactNode }) {
  return <p className="eyebrow mb-2">{children}</p>;
}
